import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import { useCoachNote, useSessionReview, type SessionReview, type TopicFact } from 'modules/studyPlan';
import { catalogLink, messageLanguage, testCost } from 'modules/studyPlan/labels';
import { TopicList } from './TopicList';

/** A result finished within this long ago is "at the end of a session": the coach note is asked for on its own. */
const FRESH_MS = 24 * 60 * 60 * 1000;

const sameTopic = (a: { subject: string; topic: string }, b: { subject: string; topic: string }) =>
  a.subject === b.subject && a.topic.trim().toLowerCase() === b.topic.trim().toLowerCase();

const linkClass = 'font-medium text-foreground underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function NextStep({ next }: { next: NonNullable<SessionReview['next']> }) {
  const { t } = useTranslation();
  const upcoming = next.status === 'DONE' ? next.tomorrow : next.test;
  const catalog = catalogLink(next.goal, t);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="min-w-0 text-sm text-foreground">
        {upcoming
          ? t(next.status === 'DONE' ? 'studyPlan.review_next_tomorrow' : 'studyPlan.review_next_today', { title: upcoming.title })
          : t('studyPlan.review_next_none')}
        {upcoming && <span className="text-muted-foreground"> · {testCost(upcoming, t)}</span>}
      </p>
      {upcoming && next.status !== 'DONE' ? (
        <Button asChild size="sm">
          <Link to={`/portal/tests/take/${upcoming.id}`}>{t('studyPlan.start')}</Link>
        </Button>
      ) : !upcoming ? (
        <Link to={catalog.to} className={linkClass}>{catalog.label}</Link>
      ) : null}
    </div>
  );
}

/**
 * The end-of-session review on a candidate's result page: how the attempt went against
 * their target, topic by topic, and what to do next. Everything but the coach's wording
 * comes straight from the learner's own answers; the wording is generated once per
 * attempt and the page reads fine without it.
 */
export function SessionReviewCard({ attemptId }: { attemptId: string }) {
  const { t, i18n } = useTranslation();
  const { data: review, isLoading, isFetching } = useSessionReview(attemptId);
  const { mutateAsync: requestCoach } = useCoachNote(attemptId);
  const askedFor = useRef<string | null>(null);
  // The attempt whose request ended without a note, so the placeholder does not wait for one that is not coming.
  const [gaveUp, setGaveUp] = useState<string | null>(null);
  const language = i18n?.language;

  const ask = () => {
    askedFor.current = attemptId;
    requestCoach(messageLanguage(language))
      .then((answer) => {
        if (!answer?.coach) setGaveUp(attemptId);
      })
      .catch(() => setGaveUp(attemptId));
  };

  const needsCoach = Boolean(review?.coachAvailable && !review.coach);
  const fresh = review ? Date.now() - Date.parse(review.finishedAt) < FRESH_MS : false;
  // Only from a fresh copy of the review (not one left in the cache), and only for a result that has just
  // finished: browsing old results must not spend the learner's daily allowance.
  const shouldAsk = needsCoach && fresh && !isFetching;
  useEffect(() => {
    if (shouldAsk && askedFor.current !== attemptId) ask();
    // `ask` reads the current language and attempt; the request is keyed by the attempt alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAsk, attemptId]);

  if (isLoading) return <Skeleton className="my-4 h-56 w-full rounded-lg" />;
  if (!review) return null;

  const { facts, coach, next } = review;
  const writing = shouldAsk && gaveUp !== attemptId;
  const offerAsk = needsCoach && !fresh && askedFor.current !== attemptId && gaveUp !== attemptId;
  const tipFor = (topic: { subject: string; topic: string }) => coach?.tips.find((tip) => sameTopic(tip, topic))?.advice;
  const gap = Math.max(0, facts.targetPercent - facts.percent);
  const nextTip = facts.nextFocus ? tipFor(facts.nextFocus) : undefined;
  // With a single topic the list would only repeat the score above it, so it needs at least two to compare.
  const showRepeat = facts.focus.length > 0 && facts.topics.length > 1;

  return (
    <section aria-labelledby="review-title" className="my-4 rounded-lg border border-border bg-card p-5 md:p-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t('studyPlan.review_eyebrow')}</p>
      <h2 id="review-title" className="mt-1 font-display text-xl font-bold tracking-[-0.02em] text-foreground">
        {t(`studyPlan.review_verdict_${facts.verdict}`, { target: facts.targetPercent, gap })}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {[
          facts.total > 0 && t('studyPlan.review_score_line', { correct: facts.correct, total: facts.total }),
          facts.pace && t(`studyPlan.review_pace_${facts.pace}`),
          facts.overall?.before != null && facts.overall.after != null && t('studyPlan.review_mastery_shift', { before: facts.overall.before, after: facts.overall.after }),
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>

      {(coach || writing || offerAsk) && (
        <div className="mt-4 rounded-md bg-primary/25 p-4">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground/80">
            <Sparkles size={14} aria-hidden="true" />
            {t('studyPlan.review_coach_label')}
          </p>
          {coach ? (
            <p className="mt-1.5 text-sm leading-relaxed text-foreground">{coach.summary}</p>
          ) : writing ? (
            <div className="mt-2 space-y-2" role="status">
              <p className="text-sm text-foreground/80">{t('studyPlan.review_coach_writing')}</p>
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : (
            <Button className="mt-2" variant="outline" size="xs" onClick={ask}>{t('studyPlan.review_coach_ask')}</Button>
          )}
        </div>
      )}

      {showRepeat && (
        <div className="mt-5">
          <TopicList
            testId="review-work"
            title={t('studyPlan.review_repeat_title')}
            empty=""
            topics={facts.focus}
            tone="work"
            adviceFor={(topic: TopicFact) => tipFor(topic) ?? t('studyPlan.review_hint', { topic: topic.topic, correct: topic.correct, total: topic.total })}
          />
        </div>
      )}

      {next && (
        <div className="mt-6 border-t border-border pt-4">
          <h3 className="text-[15px] font-semibold text-foreground">{t('studyPlan.review_next_title')}</h3>
          <div className="mt-2">
            <NextStep next={next} />
          </div>
          {facts.nextFocus && (
            <p className="mt-2 text-[13px] text-muted-foreground">
              {t('studyPlan.review_weakest', { topic: facts.nextFocus.topic, mastery: facts.nextFocus.mastery })}
              {nextTip && ` ${nextTip}`}{' '}
              <Link to="/portal/settings/plan" className={linkClass}>{t('studyPlan.review_open_plan')}</Link>
            </p>
          )}
          {!facts.nextFocus && (
            <p className="mt-2 text-[13px]">
              <Link to="/portal/settings/plan" className={linkClass}>{t('studyPlan.review_open_plan')}</Link>
            </p>
          )}
        </div>
      )}
    </section>
  );
}

export default SessionReviewCard;
